import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import {
	AddTagsAndSubscriptionsRequest,
	ClientFilterSubscriptionRequest,
	CreateClientAndBulkSubscriptionsRequest,
	ManagementBulkClientProcessControllerService,
	ManagementClientControllerService,
	ManagementClientResponse,
	ManagementClientSubscriptionControllerService,
	ManagementClientSubscriptionResponse,
} from 'api-client';
import { Subject, debounceTime, take, takeUntil } from 'rxjs';
import { NavMapComponent } from '../../../shared/components/nav-map/nav-map.component';
import { MatDividerModule } from '@angular/material/divider';
import { UtilsService } from '../../../shared/services/utils.service';
import { ManageClientComponent } from './manage-client/manage-client.component';
import { CommonModule } from '@angular/common';
import { ManagementAccountControllerService, ManagementAccountResponse } from 'api-auth';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ManagementClipControllerService, ManagementClipReportControllerService } from 'api-clipping';
import { SharedService } from '../../../shared/services/shared.service';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { Constants } from '../../../shared/interfaces/constants';
import { ClientsReportRequest, ManagementContractNoticesReportControllerService } from 'api-contract-notices';
import { MatMenuModule } from '@angular/material/menu';
import { environment } from '../../../../environments/environment';
import { EmailControllerService } from 'api-notification';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TenderiMkRequest } from '../../../../../dist/api-notification/src';

interface ManagementClientWithSelection extends ManagementClientResponse {
	checked: boolean;
}

@Component({
	selector: 'app-clients',
	standalone: true,
	imports: [
		CommonModule,
		NavMapComponent,
		MatDividerModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		MatPaginatorModule,
		MatTableModule,
		MatCheckboxModule,
		MatMenuModule,
    MatProgressSpinnerModule,
	],
	templateUrl: './clients.component.html',
	styleUrl: './clients.component.scss',
})
export class ClientsComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	externalAssets = environment.reseller.externalAssets;
	readonly DEBOUNCE_TIME = 300;
	@ViewChildren(MatPaginator) paginator = new QueryList<MatPaginator>();
	readonly PAGE_OPTIONS = [500, 1000, 2000];
	readonly ACCOUNTS_PAGE_SIZE = 20;
  apiLoading = false;

	searchFormControl: FormControl = new FormControl();
	filterSubscriptionType: FormControl = new FormControl(['CLIPPING', 'BUSINESS_INFORMATOR', 'TRANSCRIPTIONS', 'ACTIVE_SUBSCRIPTION']);
	sortReportSentAt: FormControl = new FormControl('CLIPPING');

	displayedColumnsClient: string[] = ['SELECTION', 'NAME', 'LAST_REPORT_SENT_AT', 'SUBSCRIPTIONS', 'LANG', 'ACTIONS'];
	dataSourceClient: MatTableDataSource<ManagementClientWithSelection> = new MatTableDataSource<ManagementClientWithSelection>();

	displayedColumnsSubscription: string[] = ['TYPE', 'EMAIL_TO', 'VALID_FROM', 'VALID_TO', 'ACTIONS'];
	dataSourceSubscription: MatTableDataSource<ManagementClientSubscriptionResponse> = new MatTableDataSource<ManagementClientSubscriptionResponse>();
	subscriptionTypes = Constants.CLIENT_SUBSCRIPTION_TYPES;
	private scopeOrder = Constants.SCOPES['CLIENT'];

	displayedColumnsAccounts: string[] = ['IDENTIFIER', 'NAME', 'SCOPES', 'ACTIVE'];
	dataSourceAccounts: MatTableDataSource<ManagementAccountResponse> = new MatTableDataSource<ManagementAccountResponse>();

	selectedClients: ManagementClientWithSelection[] = [];
	selectedClient: ManagementClientWithSelection | undefined;

	@ViewChild('selectAllSubscriptions') private selectAllSubscriptions!: MatOption;
	subscriptionsFilterOptions: string[] = ['CLIPPING', 'BUSINESS_INFORMATOR', 'TRANSCRIPTIONS', 'ACTIVE_SUBSCRIPTION', 'EXPIRED_SUBSCRIPTION', 'ALMOST_EXPIRED', 'NO_SUBSCRIPTION', 'SUBSCRIPTION_REQUESTS'];

	private lockAction = false;

	constructor(
		public utils: UtilsService,
		private apiClient: ManagementClientControllerService,
		private apiClientSubscription: ManagementClientSubscriptionControllerService,
		private apiClientBulkProcess: ManagementBulkClientProcessControllerService,
		private apiAuthAccounts: ManagementAccountControllerService,
		private apiClippingReport: ManagementClipReportControllerService,
		private apiClipping: ManagementClipControllerService,
		private apiContractNoticesReport: ManagementContractNoticesReportControllerService,
		private apiNotification: EmailControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private dialog: MatDialog
	) {}

	ngOnInit() {
		this.searchFormControl.valueChanges.pipe(debounceTime(this.DEBOUNCE_TIME), takeUntil(this.onDestroy)).subscribe(searchTerm => {
			this.getClients();
		});

		setTimeout(() => {
			this.getClients();
		}, 100);
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	getSubscriptionValidityStatus(validTo: Date) {
		const currentDate = new Date();
		if (validTo) {
			if (new Date(validTo) < currentDate) {
				return 'red';
			}

			if (new Date(validTo) < new Date(currentDate.setMonth(currentDate.getMonth() + 1))) {
				return 'orange';
			}
		}
		return this.utils.getScssVarValue('theme-palette-500');
	}

	itemAction(
		type: 'CLIENT' | 'SUBSCRIPTION' | 'ACCOUNT',
		action: 'NEW' | 'EDIT' | 'DETAILS' | 'ACTIVATE' | 'DEACTIVATE' | 'SEND_REPORT' | 'SEND_REPORTS' | 'EXPORT_REPORT' | 'DELETE' | 'DEV_MODE',
		client?: ManagementClientWithSelection,
		item?: any
	) {
		if (this.lockAction) {
			return;
		}

		this.lockAction = true;
		switch (action) {
			case 'DETAILS': {
				this.selectedClient = client;
				this.getClientSubscriptions();
				this.getClientAccounts();
				setTimeout(() => {
					window.scrollTo({ top: 0, behavior: 'smooth' });
					this.lockAction = false;
				}, 100);
				return;
			}
			case 'SEND_REPORT':
			case 'SEND_REPORTS': {
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
					width: '600px',
					data: {
						mode: 'USER_ACTION',
						type: 'custom-fields',
						message: this.translate.instant('CLIENT.CHOOSE_REPORT_TYPE'),
						fields: [
							{
								type: 'selection-multiple',
								data: [
									{
										name: this.translate.instant('CLIENT.SUBSCRIPTION_TYPES.CLIPPING'),
										value: 'CLIPPING',
									},
									{
										name: this.translate.instant('CLIENT.BUSINESS_INFORMATOR'),
										value: 'BUSINESS_INFORMATOR',
									},
								],
							},
							{
								type: 'date-time',
								key: 'dateFrom',
								label: 'CLIENT.DATE_FROM',
								data: null,
							},
							{
								type: 'date-time',
								key: 'dateTo',
								label: 'CLIENT.DATE_TO',
								data: null,
							},
						],
					},
				});

				dialogRef
					.afterClosed()
					.pipe(take(1))
					.subscribe(result => {
						if (result && result.data.selection) {
							if (result.data.selection.indexOf('BUSINESS_INFORMATOR') !== -1) {
								this.sendBIReport(result.data.dateFrom, result.data.dateTo, client ? client.id : undefined);
							}

							if (result.data.selection.indexOf('CLIPPING') !== -1) {
								this.sendClippingReport(result.data.dateFrom, result.data.dateTo, client ? client.id : undefined);
							}
						}
						this.lockAction = false;
					});

				return;
			}
			case 'EXPORT_REPORT': {
				if (client) {
					const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
						width: '600px',
						data: {
							mode: 'USER_ACTION',
							type: 'custom-fields',
							fields: [
								{
									type: 'date-range',
									data: [
										{
											name: '',
											value: [new Date(), new Date()],
										},
									],
								},
							],
						},
					});

					dialogRef
						.afterClosed()
						.pipe(take(1))
						.subscribe(result => {
							if (result) {
								this.exportClientReport(client, result.data.dateFrom, result.data.dateTo);
							}
							this.lockAction = false;
						});
				}
				return;
			}
			case 'DEV_MODE': {
				if (!client) {
					// Global fallback (toolbar button): empty tabs, manual paste - the
					// original flow, kept as a reserve. Always creates a brand new client,
					// unlike the per-client action below.
					const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
						width: '800px',
						data: {
							mode: 'USER_ACTION',
							type: 'custom-tabs',
							tabs: [
								{
									label: this.translate.instant('CLIENT.CREATE_CLIENT'),
									fields: [
										{
											type: 'textarea',
											key: 'clientData',
										},
									],
								},
								{
									label: this.translate.instant('CLIENT.SEND_PROFORMA_INVOICE'),
									fields: [
										{
											type: 'select',
											label: this.translate.instant('CLIENT.LANG'),
											key: 'lang',
											options: ['MK', 'EN', 'AL'],
										},
										{
											type: 'input',
											label: this.translate.instant('CLIENT.PAY_BY_LINK_URL'),
											key: 'payByLinkUrl',
										},
										{
											type: 'textarea',
											key: 'tenderiData',
										},
									],
								},
							],
						},
					});

					dialogRef
						.afterClosed()
						.pipe(take(1))
						.subscribe(result => {
							if (result && result.activeTab === 0 && result.data?.clientData) {
								this.createClientFromBulkData(result.data.clientData);
							} else if (result && result.activeTab === 1 && result.data?.lang && result.data?.payByLinkUrl && result.data?.tenderiData) {
								this.sendProformaInvoice(result.data.lang, result.data.payByLinkUrl, result.data.tenderiData);
							}
							this.lockAction = false;
						});
					return;
				}

				// Per-client action (row button): prefilled tabs for the request the backend
				// already auto-created - see CLIPPING-562.
				// LINK payment locks the "Create client" tab until the profoforma has been
				// sent - see CLIENT.SUBSCRIPTION_REQUESTS_LOCKED and CLIPPING-562.
				const invoiceStillPending = client.pendingPaymentOption === 'LINK' && !!client.pendingInvoiceJson;

				const tabs: any[] = [];
				if (client.pendingTagsSubscriptionsJson && !invoiceStillPending) {
					tabs.push({
						label: this.translate.instant('CLIENT.CREATE_CLIENT'),
						fields: [
							{
								type: 'textarea',
								key: 'clientData',
								value: client.pendingTagsSubscriptionsJson,
							},
						],
					});
				}
				if (client.pendingInvoiceJson) {
					tabs.push({
						label: this.translate.instant('CLIENT.SEND_PROFORMA_INVOICE'),
						fields: [
							{
								type: 'select',
								label: this.translate.instant('CLIENT.LANG'),
								key: 'lang',
								options: ['MK', 'EN', 'AL'],
							},
							{
								type: 'input',
								label: this.translate.instant('CLIENT.PAY_BY_LINK_URL'),
								key: 'payByLinkUrl',
							},
							{
								type: 'textarea',
								key: 'tenderiData',
								value: client.pendingInvoiceJson,
							},
						],
					});
				}

				if (!tabs.length) {
					this.lockAction = false;
					return;
				}

				const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
					width: '800px',
					data: {
						mode: 'USER_ACTION',
						type: 'custom-tabs',
						tabs,
					},
				});

				dialogRef
					.afterClosed()
					.pipe(take(1))
					.subscribe(result => {
						if (result && result.data?.clientData) {
							this.addTagsAndSubscriptionsToClient(client.id, result.data.clientData);
						} else if (result && result.data?.lang && result.data?.payByLinkUrl && result.data?.tenderiData) {
							this.sendProformaInvoice(result.data.lang, result.data.payByLinkUrl, result.data.tenderiData);
						}
						this.lockAction = false;
					});
				return;
			}
		}

		const dialogRef = this.dialog.open(ManageClientComponent, {
			width: type !== 'SUBSCRIPTION' ? '600px' : '800px',
			data: {
				mode: action,
				type,
				client,
				item,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result === true) {
					const currentSelectedClient = this.selectedClient;
					this.getClients();

					if (type === 'CLIENT' && action === 'DELETE') {
						this.selectedClient = undefined;
					} else if (currentSelectedClient && (type === 'SUBSCRIPTION' || type === 'ACCOUNT')) {
						setTimeout(() => {
							this.selectedClient = this.dataSourceClient.data.find(c => c.id === currentSelectedClient.id);
							if (this.selectedClient) {
								this.getClientSubscriptions();
								this.getClientAccounts();
							}
						}, 100);
					}
				}

				if (this.selectedClient && !(type === 'CLIENT' && action === 'DELETE')) {
					this.getClientSubscriptions();
					this.getClientAccounts();
				}

				this.lockAction = false;
			});
	}

	/** Client methods */
	selectAllClients(event: MatCheckboxChange) {
		this.selectedClients = [];
		this.dataSourceClient.data.forEach(d => {
			d.checked = event.checked;
			this.checkItemClient(event, d);
		});
	}
	checkItemClient(event: MatCheckboxChange, client: ManagementClientWithSelection) {
		if (event.checked) {
			this.selectedClients.push(client);
		} else {
			this.selectedClients = this.selectedClients.filter(c => c.id !== client.id);
		}
	}

	getClients(preselect?: string[], itemSelected?: Event | 'ALL') {
		this.selectedClient = undefined;
		const pagination = {
			index: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageIndex ? this.paginator.toArray()[0].pageIndex : 0,
			pageSize: this.paginator.toArray()[0] && this.paginator.toArray()[0].pageSize ? this.paginator.toArray()[0].pageSize : this.PAGE_OPTIONS[0],
		};

		let subscriptions: any[] = [];
		let statuses: any[] =  [];
		let almostExpired = false;
		let pendingRequestsOnly = false;

		this.filterSubscriptionType.value.forEach((item: string) => {
			if (item === 'ACTIVE_SUBSCRIPTION') {
				statuses.push('ACTIVE');
			} else if (item === 'EXPIRED_SUBSCRIPTION') {
				statuses.push('EXPIRED');
			} else if (item === 'NO_SUBSCRIPTION') {
				statuses.push('NO_SUBSCRIPTION');
			} else if (item === 'ALMOST_EXPIRED') {
				almostExpired = true;
			} else if (item === 'SUBSCRIPTION_REQUESTS') {
				pendingRequestsOnly = true;
			} else if (item === 'BUSINESS_INFORMATOR') {
				subscriptions.push.apply(subscriptions, Constants.CLIENT_SUBSCRIPTION_TYPES_BUSINESS_INFORMATOR);
			} else if (item === 'CLIPPING') {
				subscriptions.push('CLIPPING');
			} else if (item === 'TRANSCRIPTIONS') {
				subscriptions.push('TRANSCRIPTIONS');
			}
		});


		const clientFilterSubscriptionRequest: ClientFilterSubscriptionRequest = {
			name: this.searchFormControl.value,
			subscriptions,
			statuses: statuses.length === 0 ?  ["ACTIVE", "EXPIRED", "NO_SUBSCRIPTION"] : statuses,
			almostExpired,
			pendingRequestsOnly,
		};

    	this.apiLoading = true;
		this.apiClient
			.getClients(clientFilterSubscriptionRequest, pagination.index, pagination.pageSize, this.sortReportSentAt.value)
			.pipe(take(1))
			.subscribe({
				next: response => {
          this.apiLoading = false;
					if (response.content) {
						this.dataSourceClient.data = response.content as ManagementClientWithSelection[];
						this.paginator.toArray()[0].length = response.totalElements || 0;
						this.selectedClients = [];

						// if user is does not have subscription do not show lastSend report
						const businessInformatorSubscriptions = [
							// 'ANALYTICS',
							'CONTRACT_NOTICES',
							'CONTRACT_NOTICES_KOSOVO',
							'CONTRACT_NOTICES_ALBANIA',
							'CONTRACT_NOTICES_ANNOUNCEMENTS',
							'CONTRACT_NOTICES_ANNOUNCEMENTS_KOSOVO',
							'CONTRACT_NOTICES_ANNOUNCEMENTS_ALBANIA',
							'SALES',
							'BANKRUPTCIES',
						];
						this.dataSourceClient.data.forEach(d => {
							if (!d.subscriptions?.find(s => s.type === 'CLIPPING')) {
								d.lastClippingReportSentAt = undefined;
							} else if (!d.subscriptions?.find(s => businessInformatorSubscriptions.includes(s.type || ''))) {
								d.lastBiReportSentAt = undefined;
							}
						});

						if (preselect) {
							this.dataSourceClient.data.forEach(d => {
								if (preselect.indexOf(d.id) !== -1) {
									d.checked = true;
									this.selectedClients.push(d);
								}
							});
						}
					}
				},
				error: () => {
					this.apiLoading = false;
				},
			});
	}

	private sendBIReport(dateFrom: Date, dateTo: Date, clientId?: string) {
		const clientsReportRequest: ClientsReportRequest = {
			clientIds: clientId ? [clientId] : this.selectedClients.map(c => c.id),
			dateFrom,
			dateTo,
		};
    this.apiLoading = true;
		this.apiContractNoticesReport
			.sendReportFromClients(clientsReportRequest)
			.pipe(take(1))
			.subscribe({
				next: response => {
          this.apiLoading = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.REPORT_SENT'),
						type: 'SUCCESS',
					});
					let unsuccessSendClients = response.filter(item => item.statusCode !== 200 && item.statusCode !== 204);
					let preselectedClients: any[] = [];
					if (unsuccessSendClients && unsuccessSendClients.length) {
						preselectedClients = unsuccessSendClients.map(item => item.clientId);
					}
					this.getClients(preselectedClients);
					this.lockAction = false;
				},
				error: () => {
          this.apiLoading = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.lockAction = false;
				},
			});
	}

	private sendClippingReport(dateFrom: Date, dateTo: Date, clientId?: string) {
		const clientsReportRequest: ClientsReportRequest = {
			clientIds: clientId ? [clientId] : this.selectedClients.map(c => c.id),
			dateFrom,
			dateTo,
		};
    this.apiLoading = true;
		this.apiClippingReport
			.sendReportFromClients(clientsReportRequest)
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
          this.apiLoading = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.REPORT_SENT'),
						type: 'SUCCESS',
					});
					let unsuccessSendClients = response.filter((item: any) => item.statusCode !== 200 && item.statusCode !== 204);
					let preselectedClients: any[] = [];
					if (unsuccessSendClients && unsuccessSendClients.length) {
						preselectedClients = unsuccessSendClients.map((item: any) => item.clientId);
					}
					this.getClients(preselectedClients);
					this.lockAction = false;
				},
				error: () => {
          this.apiLoading = false;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.lockAction = false;
				},
			});
	}

	private exportClientReport(client: ManagementClientResponse, dateFrom?: Date, dateTo?: Date) {
		this.apiClipping
			.exportClips({
				clientId: client.id,
				dateFrom,
				dateTo,
			})
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					const content = new Blob([response as BlobPart], { type: 'application/vnd.ms-excel' });
					const fileURL = URL.createObjectURL(content);
					window.open(fileURL, '_blank');
					this.lockAction = false;
				},
			});
	}

	// Global fallback (toolbar {} button): always creates a brand new client from pasted
	// JSON - kept as a manual reserve for cases the auto-create flow doesn't cover.
	private createClientFromBulkData(data: string) {
		const request: CreateClientAndBulkSubscriptionsRequest = JSON.parse(data);
		this.apiClientBulkProcess
			.createClientSubscriptionsBulk(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.getClients();
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private addTagsAndSubscriptionsToClient(clientId: string, data: string) {
		const request: AddTagsAndSubscriptionsRequest = JSON.parse(data);
		this.apiClientBulkProcess
			.addTagsAndSubscriptionsToClient(clientId, request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.getClients();
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private sendProformaInvoice(lang: string, payByLinkUrl: string, data: string) {
		const request: TenderiMkRequest = JSON.parse(data);
		request.payByLinkUrl = payByLinkUrl;
		this.apiNotification
			.processPayByLinkData(request, lang)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.getClients();
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}
	/** (END)Client methods */

	/** Subscriptions methods */
	private getClientSubscriptions() {
		if (this.selectedClient) {
			this.apiClientSubscription
				.getClientSubscriptionsMgmt(this.selectedClient.id)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.dataSourceSubscription.data = response;
					},
				});
		}
	}
	/** (END)Subscriptions methods */

	/** Accounts methods */
	getClientAccounts() {
		const pagination = {
			index: this.paginator.toArray()[1] && this.paginator.toArray()[1].pageIndex ? this.paginator.toArray()[1].pageIndex : 0,
			pageSize: this.ACCOUNTS_PAGE_SIZE,
		};

		if (this.selectedClient) {
			this.apiAuthAccounts
				.getClientAccounts(this.selectedClient?.id, pagination.index, pagination.pageSize)
				.pipe(take(1))
				.subscribe({
					next: response => {
						if (response.content) {
							this.dataSourceAccounts.data = response.content;
							this.paginator.toArray()[1].length = response.totalElements || 0;
						}
					},
				});
		}
	}
	sortScopes(scopes: string[]): string[] {
		return [...scopes].sort((a, b) => this.scopeOrder.indexOf(a) - this.scopeOrder.indexOf(b));
	}
	/** (END)Accounts methods */
}
